// Preloader state
let preloaderInitialized = false;

/**
 * Wait for critical images to load (non-lazy images only)
 */
function waitForAllImages() {
  return new Promise((resolve) => {
    const isMobile = window.innerWidth <= 999;

    // Only wait for critical images
    const images = document.querySelectorAll("img:not([loading='lazy'])");

    const totalImages = images.length;
    let loadedImages = 0;

    if (totalImages === 0) {
      resolve();
      return;
    }

    // Global timeout safety
    const globalTimeout = setTimeout(() => {
      resolve();
    }, isMobile ? 2000 : 4000);

    const checkComplete = () => {
      loadedImages++;

      if (loadedImages === totalImages) {
        clearTimeout(globalTimeout);
        resolve();
      }
    };

    images.forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) {
        checkComplete();
      } else {
        img.addEventListener("load", checkComplete, { once: true });
        img.addEventListener("error", checkComplete, { once: true });
      }
    });
  });
}

/**
 * Hide preloader
 */
function hidePreloader() {
  const preloader = document.getElementById("preloader");

  if (!preloader) return;

  preloader.classList.add("hidden");

  document.body.style.overflow = "";
  document.body.classList.remove("preloader-active");

  document.documentElement.style.overflow = "";
  document.documentElement.classList.remove("preloader-active");

  document.dispatchEvent(new CustomEvent("preloaderFinished"));

  setTimeout(() => {
    preloader.remove();
  }, 500);
}

/**
 * Initialize preloader
 */
function initializePreloader() {
  if (preloaderInitialized) return;
  preloaderInitialized = true;

  document.body.style.overflow = "hidden";
  document.body.classList.add("preloader-active");

  document.documentElement.style.overflow = "hidden";
  document.documentElement.classList.add("preloader-active");

  const startTime = Date.now();

  // Prevent flicker
  const minDisplayTime = 150;

  waitForAllImages().then(() => {
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, minDisplayTime - elapsed);

    setTimeout(() => {
      hidePreloader();
    }, remainingTime);
  });
}

// Initialize when DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePreloader);
} else {
  initializePreloader();
}

// Handle browser back/forward cache
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    preloaderInitialized = false;
    initializePreloader();
  }
});

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".initially-hidden").forEach((el) => {
    el.classList.remove("initially-hidden");
  });

  initializeLazyImages();

  // Batch DOM reads to avoid forced reflows
  requestAnimationFrame(() => {
    initializeHeader();
    initializeAnnouncement();
  });
});

function initializeAnnouncement() {
  // Get the announcement bar element
  const announcementBar = document.querySelector(".announcement-bar");
  if (!announcementBar) return;

  // Calculate its height (including padding)
  const height = announcementBar.offsetHeight;

  // Set as CSS variable on :root (globally accessible)
  window.announcementHeight = height;
  document.documentElement.style.setProperty(
    "--announcement-height",
    `${height}px`
  );
}

function initializeHeader() {
  // Get the announcement bar element
  const mainHeader = document.querySelector(".main-header");
  if (!mainHeader) return;

  // Calculate its height (including padding)
  const headerHeight = mainHeader.offsetHeight;

  // Set as CSS variable on :root (globally accessible)
  window.headerHeight = headerHeight;
  document.documentElement.style.setProperty(
    "--header-height",
    `${headerHeight}px`
  );
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function debounceImmediate(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

// Helper: Calculate luminance (brightness) of a color based on rgb values like this ' 255, 255 , 255 '
function getLuminance(colorStr) {
  const [r, g, b] = colorStr.split(",").map(Number);
  return (r * 299 + g * 587 + b * 114) / 1000; // Weighted luminance
}

// Helper function to format money
function formatMoney(cents, format) {
  if (typeof cents === "string") cents = parseInt(cents);
  const value = cents;
  return Shopify.formatMoney(value, format);
}

// Global lazy image loader with skeleton backgrounds
function initializeLazyImages() {
  const lazyImages = document.querySelectorAll(".lazy-fade");

  if (!lazyImages.length) return;

  lazyImages.forEach((img) => {
    // Add skeleton class to wrapper
    const wrapper = img.closest(".lazy-wrapper");
    if (wrapper) {
      wrapper.classList.add("loading");
    }

    // Check if already loaded
    if (img.complete && img.naturalHeight !== 0) {
      handleImageLoad(img, wrapper);
    } else {
      // Listen for load event
      img.addEventListener("load", function () {
        handleImageLoad(img, wrapper);
      });

      // Handle error
      img.addEventListener("error", function () {
        if (wrapper) {
          wrapper.classList.remove("loading");
          wrapper.classList.add("error");
        }
      });
    }
  });
}

function handleImageLoad(img, wrapper) {
  img.classList.add("loaded");
  if (wrapper) {
    wrapper.classList.remove("loading");
    wrapper.classList.add("loaded");
  }
}

var Shopify = Shopify || {};
// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.money_format = "${{amount}}";
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};
